import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, LOCALE_ID, RendererFactory2 } from '@angular/core';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  MentionMeConfig,
  MentionMeParameters,
  MentionMeSituation,
} from '@checkout/util-models';
import {
  AuthFacade,
  userToCustomerDetails,
} from '@domgen/dgx-fe-authentication';
import {
  CustomerDetails,
  filterNullUndefined,
  httpParamsFromObject,
} from '@domgen/dgx-fe-common';
import { LocaleId } from '@domgen/dgx-fe-i18n';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, first, map, switchMap, tap } from 'rxjs';

interface ComponentState {
  customerDetails?: CustomerDetails;
  parameters?: MentionMeParameters;
}

@Injectable()
export class MentionMeService extends ComponentStore<ComponentState> {
  private readonly customerDetails$: Observable<CustomerDetails | undefined> =
    this.select((state: ComponentState) => state.customerDetails);

  private readonly parameters$: Observable<MentionMeParameters | undefined> =
    this.select((state: ComponentState) => state.parameters);

  private readonly renderer = this.rendererFactory.createRenderer(null, null);

  constructor(
    @Inject(CHECKOUT_CONFIG)
    private readonly checkoutConfig: CheckoutConfig,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LOCALE_ID) private readonly localeId: LocaleId,
    private readonly authFacade: AuthFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly rendererFactory: RendererFactory2
  ) {
    super({});
  }

  private readonly updateCustomerDetails = this.updater(
    (state: ComponentState, customerDetails?: CustomerDetails) => ({
      ...state,
      customerDetails,
    })
  )(
    combineLatest({
      user: this.authFacade.user$,
      customerDetails: this.checkoutFacade.customerDetails$,
    }).pipe(
      map(({ user, customerDetails }) => {
        if (customerDetails) {
          return customerDetails;
        }
        if (user) {
          return userToCustomerDetails(user);
        }
        return undefined;
      })
    )
  );

  private readonly updateMentionMeParameters = this.updater(
    (state: ComponentState, parameters?: MentionMeParameters) => ({
      ...state,
      parameters,
    })
  )(
    this.customerDetails$.pipe(
      map((customerDetails: CustomerDetails | undefined) => {
        const situation = this.config?.situation;

        if (!situation || !customerDetails) {
          return undefined;
        }

        const parameters: MentionMeParameters = {
          situation,
          locale: this.localeId,
        };

        if (situation === MentionMeSituation.PostPurchaseMainsite) {
          return parameters;
        }

        return {
          ...parameters,
          firstName: customerDetails.firstName,
          surname: customerDetails.surname,
          email: customerDetails.email?.main,
          signup_id: `${customerDetails.firstName.toLowerCase()}-${Date.now()}`,
        };
      })
    )
  );

  readonly init = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      first(),
      switchMap(() => this.parameters$),
      filterNullUndefined(),
      first(),
      tap((parameters: MentionMeParameters) =>
        this.appendMentionMeScript(parameters)
      )
    )
  );

  private appendMentionMeScript(parameters: MentionMeParameters): void {
    const { mentionMeApi, referrerOffer } = this.config ?? {};

    if (!mentionMeApi || !referrerOffer) {
      return;
    }

    const httpParams = httpParamsFromObject(parameters as any);

    const script: HTMLScriptElement = this.renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = `${mentionMeApi}/referreroffer/${referrerOffer}?${httpParams.toString()}`;

    this.renderer.appendChild(this.document.body, script);
  }

  private get config(): MentionMeConfig | undefined {
    return this.checkoutConfig.mentionMe;
  }
}
